import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Group,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { PiInfo, PiPaperPlaneTilt } from 'react-icons/pi';
import Drawer from '@/components/drawer/Drawer';
import { TOffer } from '@/models/offers';
import { ScheduleState } from './ScheduleStateBadge';
import SplitScheduleBuilder from './SplitScheduleBuilder';
import { DraftInstallment, OrderedService, SplitDerived } from './splitDerive';

export default function InstallmentPlannerDrawer({
  opened,
  onClose,
  status,
  editable,
  installments,
  setInstallments,
  services,
  currency,
  accountId,
  derived,
  offer,
  saving,
  proposing,
  onSave,
  onPropose,
  showCogs = false,
}: {
  opened: boolean;
  onClose: () => void;
  status: ScheduleState;
  editable: boolean;
  installments: DraftInstallment[];
  setInstallments: (next: DraftInstallment[]) => void;
  services: OrderedService[];
  currency: string;
  accountId: number;
  derived: SplitDerived;
  offer: TOffer;
  saving: boolean;
  proposing: boolean;
  onSave: () => void;
  onPropose: (note: string) => void;
  /** BizDev reviewers (approve-split) see per-service COGS; SRs never do. */
  showCogs?: boolean;
}) {
  const isMobile = useMediaQuery('(max-width: 50em)');
  const [note, setNote] = useState('');
  const d = derived;

  const submitReason = !d.amountsSumToTotal
    ? 'Installment amounts must add up to the order total'
    : '';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Installment plan"
      size={isMobile ? '100%' : '60%'}
    >
      <Box p="md">
        <SplitScheduleBuilder
          installments={installments}
          setInstallments={setInstallments}
          editable={editable}
          services={services}
          currency={currency}
          accountId={accountId}
          derived={d}
          offer={offer}
          showCogs={showCogs}
          showAssignment={false}
          // Read-only here: the SR sees what BizDev told the customer about each
          // installment, but the text is BizDev's to write.
          showNote
        />

        {editable && (
          <Textarea
            mt="md"
            label="Note to BizDev"
            placeholder="Customer context, special requests, constraints..."
            autosize
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
        )}

        {status === 'approved' && (
          <Alert
            color="blue"
            mt="md"
            icon={<PiInfo size={16} />}
            title="Awaiting customer acceptance"
          >
            <Text fz="sm">
              This schedule has been approved. Invoices will be issued once the
              customer accepts.
            </Text>
          </Alert>
        )}
      </Box>

      {editable && (
        <Box
          p="md"
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'var(--mantine-color-body)',
            borderTop: '1px solid var(--mantine-color-gray-2)',
          }}
        >
          <Group gap="xs" data-testid="ux-split-actions">
            <Tooltip label={submitReason} disabled={!submitReason}>
              <Button
                leftSection={<PiPaperPlaneTilt size={14} />}
                disabled={!d.canSubmit}
                loading={saving || proposing}
                onClick={() => onPropose(note)}
                data-testid="ux-submit-split"
              >
                {status === 'returned'
                  ? 'Resubmit schedule'
                  : 'Submit for BizDev approval'}
              </Button>
            </Tooltip>
            <Button variant="default" loading={saving} onClick={onSave}>
              Save draft
            </Button>
          </Group>
        </Box>
      )}
    </Drawer>
  );
}
